import { datadogRum } from '@datadog/browser-rum'
import appConstants from '~/libs/constants/application-constants.js'
import { useRootStore } from '~/store/root'
import { markPageLeaving } from '~/composables/pageExit'

const { nuxtBaseUrl } = appConstants

// Rejects an absolute URL, matching params-to-cookies.js's own r-param check.
export function safeRelativeRedirectPath(candidate) {
  const path = Array.isArray(candidate) ? candidate[0] : candidate
  if (!path || typeof path !== 'string') return null

  try {
    new URL(path)
    return null // parsed as an absolute URL -- reject
  } catch {
    return path
  }
}

// Must match the key identity-widget's storeLoginToken() writes to.
const WIDGET_LOGIN_TOKEN_STORAGE_KEY = 'identityWidgetLoginToken'

export function consumeWidgetLoginToken() {
  const token = sessionStorage.getItem(WIDGET_LOGIN_TOKEN_STORAGE_KEY)
  if (token) sessionStorage.removeItem(WIDGET_LOGIN_TOKEN_STORAGE_KEY)
  return token
}

function firstQueryValue(value) {
  return Array.isArray(value) ? value[0] : value
}

export function widgetRedirectOptionsFor(pageName, query = {}) {
  const email = firstQueryValue(query.email) || firstQueryValue(query.e)
  return {
    email: email || undefined,
    view: pageName === 'forgot-password' ? 'forgot-password' : undefined,
  }
}

// identity-widget removes this key from sessionStorage when it loads, so a host that serves the widget at /login never reads as a bounce.
const WIDGET_REDIRECT_MARKER_KEY = 'identityWidgetRedirectAt'
const WIDGET_BOUNCE_MARKER_KEY = 'identityWidgetBounceAt'
const WIDGET_BOUNCE_WINDOW_MS = 60000
// Reported with trigger: params = host doesn't serve the identity widget at /login (routing gap); marker = returned without signing in since this tab's last widget redirect.
const WIDGET_BOUNCE_MESSAGE = 'Identity widget /login bounced back'

let bounceReported = false
let redirectIssuedThisPageLoad = false
let bounceDetectedThisPageLoad = false

function withStorage(action, fallback) {
  try {
    return action()
  } catch {
    return fallback
  }
}

const UNREADABLE_MARKER = null
const readRedirectMarker = () => withStorage(() => Number(sessionStorage.getItem(WIDGET_REDIRECT_MARKER_KEY)) || 0, UNREADABLE_MARKER)
const writeRedirectMarker = () => withStorage(() => sessionStorage.setItem(WIDGET_REDIRECT_MARKER_KEY, String(Date.now())))
const clearRedirectMarker = () => withStorage(() => sessionStorage.removeItem(WIDGET_REDIRECT_MARKER_KEY))
const readBounceMarker = () => withStorage(() => Number(sessionStorage.getItem(WIDGET_BOUNCE_MARKER_KEY)) || 0, 0)
const writeBounceMarker = () => withStorage(() => sessionStorage.setItem(WIDGET_BOUNCE_MARKER_KEY, String(Date.now())))

export function clearWidgetRedirectMarker() {
  clearRedirectMarker()
  withStorage(() => sessionStorage.removeItem(WIDGET_BOUNCE_MARKER_KEY))
}

const isWithinBounceWindow = (marker) => Date.now() - marker < WIDGET_BOUNCE_WINDOW_MS

function hasRecentWidgetRedirect() {
  const marker = readRedirectMarker()
  return marker !== UNREADABLE_MARKER && isWithinBounceWindow(marker)
}

function mayHaveRecentWidgetRedirect() {
  const marker = readRedirectMarker()
  return marker === UNREADABLE_MARKER || isWithinBounceWindow(marker)
}

function navigationType() {
  return withStorage(() => performance.getEntriesByType('navigation')[0]?.type, undefined)
}

export function isWidgetLoginBounce(to) {
  return to?.path === '/login'
    && !!firstQueryValue(to.query?.return_to)
    && !!firstQueryValue(to.query?.sso_return_path)
    && mayHaveRecentWidgetRedirect()
}

function reportWidgetLoginBounce(path, trigger) {
  if (bounceReported) return
  bounceReported = true
  const context = { host: window.location.host, path, trigger }
  window.log?.error(WIDGET_BOUNCE_MESSAGE, context)
  datadogRum.addError(new Error(WIDGET_BOUNCE_MESSAGE), context)
}

function currentRoute() {
  const { pathname, search } = window.location
  const prefix = nuxtBaseUrl && pathname.startsWith(`${nuxtBaseUrl}/`) ? nuxtBaseUrl : ''
  return {
    path: pathname.slice(prefix.length),
    query: Object.fromEntries(new URLSearchParams(search)),
  }
}

export function redirectToWidget(returnToPath, options, { userInitiated = false } = {}) {
  const rootStore = useRootStore()
  if (rootStore.isLoggedIn) {
    return navigateTo(rootStore.getRedirectUrl())
  }

  const route = currentRoute()
  if (rootStore.isEmbedded || route.path.startsWith('/embedded')) {
    throw createError({
      statusCode: 401,
      statusMessage: 'Unauthorized Action',
      data: { isEmbedded: true, location: 'IdentityRedirect' },
      fatal: true,
    })
  }

  const loadType = navigationType()
  const backForward = loadType === 'back_forward'
  const restoredOrReloaded = backForward || loadType === 'reload'
  const backForwardAfterBounce = backForward && isWithinBounceWindow(readBounceMarker())
  const paramBounce = !backForward && isWidgetLoginBounce(route)
  const markerBounce = !restoredOrReloaded && !userInitiated && !redirectIssuedThisPageLoad && hasRecentWidgetRedirect()
  if (paramBounce || markerBounce) {
    clearRedirectMarker()
    writeBounceMarker()
    reportWidgetLoginBounce(route.path, paramBounce ? 'params' : 'marker')
  }
  if (paramBounce || markerBounce || backForwardAfterBounce || bounceDetectedThisPageLoad) {
    bounceDetectedThisPageLoad = true
    throw createError({
      statusCode: 401,
      statusMessage: 'Sign-in unavailable',
      data: { reason: 'sign-in-unavailable' },
      fatal: true,
    })
  }

  writeRedirectMarker()
  redirectIssuedThisPageLoad = true
  markPageLeaving()
  return navigateTo(identityWidgetLoginUrl(returnToPath, options), { external: true })
}

// For clicks and store actions, which run outside route middleware where a thrown error is not rendered.
export function redirectToWidgetFromAction(returnToPath, options, { userInitiated = true } = {}) {
  try {
    return redirectToWidget(returnToPath, options, { userInitiated })
  } catch (error) {
    return showError(error)
  }
}

// Must match store/identity.js's relayState computed exactly, or SAML SSO login breaks.
const SSO_RETURN_PATH = `${nuxtBaseUrl}/identity/sso`

export function identityWidgetLoginUrl(returnToPath, { email, view } = {}) {
  const { origin } = window.location
  const url = new URL(`${origin}/login`)

  // this app runs under nuxtBaseUrl (/bp); the widget needs it added back in
  url.searchParams.set('return_to', `${origin}${nuxtBaseUrl}${returnToPath || '/resume-app'}`)
  url.searchParams.set('sso_return_path', SSO_RETURN_PATH)
  if (email) url.searchParams.set('email', email)
  if (view) url.searchParams.set('view', view)
  return url.toString()
}
